/**
 * Lo que toca APIs del navegador para push (service worker, permisos, suscripción). Separado de `push.ts` (lógica
 * pura) para poder reemplazarlo en los tests con `vi.mock`.
 */
import type { DispositivoNuevo } from "./api";
import { claveABytes, nombreDispositivo, type EntornoPush } from "./push";

/** Registra `/sw.js`. Si falla (navegador viejo, modo privado) la app sigue andando sin avisos al celular. */
export function registrarServiceWorker(): void {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.register("/sw.js").catch(() => undefined);
}

export function estaInstalada(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return window.matchMedia?.("(display-mode: standalone)").matches === true || nav.standalone === true;
}

const soportaPush = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

async function suscripcionActual(): Promise<PushSubscription | null> {
  if (!soportaPush()) return null;
  const registro = await navigator.serviceWorker.getRegistration();
  return (await registro?.pushManager.getSubscription()) ?? null;
}

export async function leerEntorno(): Promise<EntornoPush> {
  const soporta = soportaPush();
  return {
    userAgent: navigator.userAgent,
    instalada: estaInstalada(),
    soportaPush: soporta,
    permiso: soporta ? Notification.permission : null,
    endpoint: (await suscripcionActual())?.endpoint ?? null,
  };
}

/** Pide el permiso (tiene que llamarse desde un toque de la persona) y suscribe este navegador. */
export async function suscribir(clave: string): Promise<DispositivoNuevo> {
  if ((await Notification.requestPermission()) !== "granted") throw new Error("permiso");
  const registro = await navigator.serviceWorker.ready;
  const s = await registro.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: claveABytes(clave) as BufferSource });
  const { endpoint, keys } = s.toJSON();
  return { endpoint: endpoint!, p256dh: keys!.p256dh ?? "", auth: keys!.auth ?? "", nombre: nombreDispositivo(navigator.userAgent) };
}

export async function desuscribir(): Promise<string | null> {
  const s = await suscripcionActual();
  if (!s) return null;
  await s.unsubscribe();
  return s.endpoint;
}
